import React, { forwardRef } from 'react';
import { TextField as MuiTextField, InputAdornment, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled TextField component
const StyledTextField = styled(MuiTextField)(({ theme, inputSize }) => ({
  '& .MuiInputBase-root': {
    borderRadius: theme.shape.borderRadius,
    
    // Size variations
    ...(inputSize === 'large' && {
      padding: '12px 16px',
      fontSize: '1rem',
    }),
    ...(inputSize === 'small' && {
      padding: '4px 8px',
      fontSize: '0.875rem',
    }),
  },
  
  // Label styling
  '& .MuiInputLabel-root': {
    fontSize: inputSize === 'small' ? '0.875rem' : '1rem',
  },
  
  // Helper text styling
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginTop: 4,
  },
}));

/**
 * TextField Component
 * A reusable text field component that wraps Material-UI TextField with additional features
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Input label
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.size - Input size (small, medium, large)
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.helperText - Helper text to display below input
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.error - Whether to show error state
 * @param {React.ReactNode} props.startAdornment - Content to show at the start of the input
 * @param {React.ReactNode} props.endAdornment - Content to show at the end of the input
 * @param {string} props.type - Input type (text, password, etc.)
 * @param {Object} props.sx - Additional styles
 * @param {string} props.description - Optional description text displayed above the field
 * @returns {React.ReactElement} Styled text field component
 */
const TextField = forwardRef(({
  label,
  value,
  onChange,
  size = 'medium',
  placeholder,
  helperText,
  required = false,
  error = false,
  startAdornment,
  endAdornment,
  type = 'text',
  sx = {},
  description,
  ...rest
}, ref) => {
  // Map component size to MUI size
  const mappedSize = {
    small: 'small',
    medium: 'medium',
    large: 'medium', // MUI doesn't have 'large', we'll style it ourselves
  }[size];
  
  return (
    <Box sx={{ width: '100%' }}>
      {/* Optional description text */}
      {description && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mb: 1 }}
        >
          {description}
        </Typography>
      )}
      
      <StyledTextField
        ref={ref}
        label={label}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        helperText={helperText}
        required={required}
        error={error}
        type={type}
        size={mappedSize}
        inputSize={size}
        sx={sx}
        InputProps={{
          startAdornment: startAdornment && (
            <InputAdornment position="start">{startAdornment}</InputAdornment>
          ),
          endAdornment: endAdornment && (
            <InputAdornment position="end">{endAdornment}</InputAdornment>
          ),
        }}
        {...rest}
      />
    </Box>
  );
});

// Display name for debugging
TextField.displayName = 'TextField';

export default TextField;